import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Content, ContentType } from "@/lib/schema";
import { cn } from "@/lib/utils";
import {
  Archive,
  BookOpen,
  ChefHat,
  Clock,
  Edit,
  Eye,
  FileText,
  MoreVertical,
  Route,
  Sparkles,
  User,
  Video,
} from "lucide-react";

interface ContentCardProps {
  content: Content;
  onEdit?: (content: Content) => void;
  onPreview?: (content: Content) => void;
  onArchive?: (content: Content) => void;
}

type ContentCategory =
  | "meditation"
  | "breathwork"
  | "movement"
  | "sleep"
  | "focus"
  | "energy"
  | "relaxation"
  | "self-love"
  | "confidence"
  | "healing"
  | "gratitude"
  | "mindfulness";

const contentTypeIcons: Partial<Record<ContentType, React.ElementType>> = {
  blog: FileText,
  invigoration: Sparkles,
  video: Video,
  recipe: ChefHat,
  journey: Route,
  journal_entry: BookOpen,
};

const contentTypeLabels: Partial<Record<ContentType, string>> = {
  blog: "Blog",
  invigoration: "Invigoration",
  video: "Video",
  recipe: "Recipe",
  journey: "Journey",
  journal_entry: "Journal Entry",
};

const categoryLabels: Record<ContentCategory, string> = {
  meditation: "Meditation",
  breathwork: "Breathwork",
  movement: "Movement",
  sleep: "Sleep",
  focus: "Focus",
  energy: "Energy",
  relaxation: "Relaxation",
  "self-love": "Self-Love",
  confidence: "Confidence",
  healing: "Healing",
  gratitude: "Gratitude",
  mindfulness: "Mindfulness",
};

const statusConfig: Record<string, { label: string; className: string }> = {
  draft: {
    label: "Draft",
    className:
      "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  },
  submitted_for_review: {
    label: "Submitted",
    className:
      "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  },
  pending_review: {
    label: "Pending Review",
    className:
      "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  },
  changes_requested: {
    label: "Changes Requested",
    className:
      "bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
  },
  approved: {
    label: "Approved",
    className:
      "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
  },
  published: {
    label: "Live",
    className:
      "bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400",
  },
  rejected: {
    label: "Rejected",
    className: "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400",
  },
  archived: {
    label: "Archived",
    className:
      "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500",
  },
};

function formatDuration(seconds?: number | null): string {
  if (!seconds) return "";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (secs === 0) return `${mins}m`;
  return `${mins}m ${secs}s`;
}

export function ContentCard({
  content,
  onEdit,
  onPreview,
  onArchive,
}: ContentCardProps) {
  const Icon = contentTypeIcons[content.type as ContentType] || Sparkles;
  const typeLabel =
    contentTypeLabels[content.type as ContentType] || content.type;
  const status = statusConfig[content.status] || statusConfig.draft;
  const categoryLabel = content.category
    ? categoryLabels[content.category as ContentCategory] || content.category
    : null;
  const thumbnailUrl = content.mainPhotoUrl || content.thumbnailUrl;

  const hasDuration =
    (content.type === "invigoration" || content.type === "video") &&
    content.duration;
  const hasCategory =
    (content.type === "invigoration" ||
      content.type === "video" ||
      content.type === "recipe" ||
      content.type === "journey") &&
    categoryLabel;
  const hasTotalDays = content.type === "journey" && content.totalDays;
  const hasJournalType =
    content.type === "journal_entry" && content.journalType;

  return (
    <Card className="group overflow-hidden hover-elevate active-elevate-2 transition-all duration-200 border-0 shadow-sm hover:shadow-md">
      <CardContent className="p-0">
        {/* Thumbnail or Icon Header */}
        <div className="h-28 bg-gradient-to-br from-pink-50 to-orange-50 dark:from-pink-950/20 dark:to-orange-950/20 flex items-center justify-center">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={content.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-white/80 dark:bg-white/10 flex items-center justify-center shadow-sm">
              <Icon className="h-6 w-6 text-pink-500" />
            </div>
          )}
        </div>

        {/* Content Info */}
        <div className="p-3 space-y-2">
          {/* Title Row with Status */}
          <div className="flex items-start justify-between gap-2">
            <h3
              className="font-medium text-sm line-clamp-2 leading-snug flex-1 min-w-0"
              data-testid={`content-title-${content.id}`}
            >
              {content.title}
            </h3>
            {/* Menu Button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 -mr-1"
                  data-testid={`content-menu-${content.id}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit?.(content)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPreview?.(content)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onArchive?.(content)}
                  className="text-destructive"
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Archive
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Coach Name */}
          {content.coachName && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <User className="h-3 w-3" />
              <span className="truncate">{content.coachName}</span>
            </div>
          )}

          {/* Status Row */}
          <div className="flex items-center gap-1.5">
            <Badge
              className={cn("text-xs font-medium border-0", status.className)}
            >
              {status.label}
            </Badge>
            {content.isFeatured && (
              <Badge className="evo-gradient text-white text-xs border-0">
                Featured
              </Badge>
            )}
          </div>

          {/* Metadata Row */}
          <div className="flex flex-wrap items-center gap-1.5">
            {/* Type Badge */}
            <Badge variant="outline" className="text-xs font-normal">
              {typeLabel}
            </Badge>

            {/* Category */}
            {hasCategory && (
              <Badge variant="secondary" className="text-xs font-normal">
                {categoryLabel}
              </Badge>
            )}

            {/* Duration */}
            {hasDuration && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
                <Clock className="h-3 w-3" />
                {formatDuration(content.duration)}
              </div>
            )}

            {/* Journey Days */}
            {hasTotalDays && (
              <Badge variant="secondary" className="text-xs font-normal">
                {content.totalDays} days
              </Badge>
            )}

            {/* Journal Type */}
            {hasJournalType && (
              <Badge
                variant="secondary"
                className="text-xs font-normal capitalize"
              >
                {content.journalType}
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
